import React, { useEffect, useRef, useState } from 'react';
import { wavelengths } from '../../data/wavelengths';
import { useActiveStep } from '../../hooks/useActiveStep';
const maskArtwork = 'https://cdn.magicpatterns.com/uploads/kyRXj4knu1WdtQLrCRih3e/mask-hero-cutout-v2.png';
type Wavelength = typeof wavelengths[number];
function LightVisual({
  item,
  compact = false
}: {
  item: Wavelength;
  compact?: boolean;
}) {
  return <figure aria-label={`White AuraGlow mask with an illustrative ${item.name.toLowerCase()} light treatment`} className={`relative isolate flex items-center justify-center overflow-hidden ${compact ? 'h-[220px]' : 'h-full min-h-0'}`} style={{
    background: `radial-gradient(ellipse at 50% 60%, ${item.glow}AA, transparent 68%)`
  }} data-id="element-345" data-mp="3uxuf6z8774ef">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-20" style={{
      backgroundImage: 'linear-gradient(#FFFFFF22 1px, transparent 1px), linear-gradient(90deg,#FFFFFF22 1px, transparent 1px)',
      backgroundSize: '56px 56px'
    }} data-id="element-346" data-mp="2t086rrcdb3w" />
      <svg aria-hidden="true" viewBox="0 0 500 500" className="absolute h-full max-h-[540px] w-full max-w-[540px] opacity-40" fill="none" data-id="element-347" data-mp="220qs07ja7tay">
        <circle cx="250" cy="250" r="180" stroke={item.color} strokeWidth="1" data-id="element-348" data-mp="26y8qt53l4s58" />
        <circle cx="250" cy="250" r="215" stroke={item.color} strokeWidth="1" strokeDasharray={item.invisible ? '3 10' : '1 0'} data-id="element-349" data-mp="2bs3gcp5vg1c8" />
        <path d="M0 250 H500 M250 0 V500" stroke={item.color} opacity=".3" data-id="element-350" data-mp="1j2nypgjpba7c" />
      </svg>
      <img src={maskArtwork} alt="" width="1226" height="1283" className={`relative z-10 block object-contain ${compact ? 'h-[190px] w-[230px]' : 'h-[82%] max-h-[455px] w-[90%]'}`} style={{
      filter: 'drop-shadow(0 20px 20px rgba(0,0,0,.28))'
    }} data-id="element-351" data-mp="1j50nodnvmktw" />
      <figcaption className="absolute inset-x-4 bottom-3 z-20 flex items-center justify-between gap-3 text-[11px] text-white/80 lg:inset-x-6 lg:bottom-5 lg:text-[12px]" data-id="element-352" data-mp="1y1hxv1kbxtev">
        <span data-id="element-353" data-mp="39eano6tk87sa">Light setting illustration</span>
        <span data-id="element-354" data-mp="39h6l7u1hwug7">{item.invisible ? 'Invisible near-infrared' : 'Visible light'}</span>
      </figcaption>
    </figure>;
}

/** Native scroll changes the selected wavelength; no wheel interception. */
export function WavelengthStory() {
  const {
    active,
    trackRef,
    stageRef,
    goToStep,
    isPinned,
    stickyTop,
    stageHeight,
    trackHeight
  } = useActiveStep(wavelengths.length);
  const [mobileActive, setMobileActive] = useState(0);
  const mobileSteps = useRef<Array<HTMLLIElement | null>>([]);
  const mobileVisual = useRef<HTMLDivElement | null>(null);
  const selected = wavelengths[isPinned ? active : mobileActive];
  useEffect(() => {
    if (isPinned) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      // Match text entry to the actual pinned illustration, including header/review offsets.
      const trigger = (mobileVisual.current?.getBoundingClientRect().bottom ?? 0) + 56;
      let next = 0;
      mobileSteps.current.forEach((step, index) => {
        if (step && step.getBoundingClientRect().top <= trigger) next = index;
      });
      setMobileActive(previous => previous === next ? previous : next);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule, {
      passive: true
    });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [isPinned]);
  return <section id="wavelengths" aria-labelledby="wavelength-title" className="border-t border-line" data-id="element-355" data-mp="ghw8qdbz61y3">
      <div className="mx-auto max-w-page px-5 pb-10 pt-16 md:px-10 lg:pt-24" data-id="element-356" data-mp="1km0dcumpc6l">
        <p className="text-[13px] font-medium uppercase tracking-[.12em] text-bronze-deep" data-id="element-357" data-mp="6j6uqvn791n4">The light behind the mask</p>
        <div className="mt-4 grid gap-6 lg:grid-cols-[1.2fr_1fr] lg:items-end" data-id="element-358" data-mp="2ma1izkjp1ul5">
          <h2 id="wavelength-title" className="max-w-2xl font-display text-[42px] font-medium leading-[1.04] text-ink md:text-[58px]" data-id="element-359" data-mp="2rb8rxmxkozte">Four wavelengths.<br data-id="element-360" data-mp="37jeytyae6z4x" />One mask.</h2>
          <p className="max-w-lg text-[16px] leading-relaxed text-ink-muted" data-id="element-361" data-mp="32qbztamuixhc">Red, blue and two near-infrared wavelengths. Scroll to explore the light spectrum and the skincare research behind it.</p>
        </div>
        <dl className="mt-9 grid grid-cols-3 gap-4 border-t border-line pt-6" data-id="element-362" data-mp="2sth5z51ipl9e">
          {[{
          value: '164',
          label: 'laser emitters'
        }, {
          value: '4',
          label: 'wavelengths'
        }, {
          value: '1064',
          label: 'nm near-infrared'
        }].map(item => <div key={item.label} data-id="element-363" data-mp="3gia6zfwzyjav"><dd className="font-display text-[36px] font-medium leading-none text-ink md:text-[48px]" data-id="element-364" data-mp="3ghh5ougt4m04">{item.value}</dd><dt className="mt-2 text-[12px] text-ink-muted md:text-[14px]" data-id="element-365" data-mp="3bjysuszo5mmu">{item.label}</dt></div>)}
        </dl>
      </div>

      <div ref={trackRef} data-wavelength-track className="relative mx-auto max-w-page px-5 md:px-10" style={{
      display: isPinned ? 'block' : 'none',
      height: trackHeight
    }} data-id="element-366" data-mp="254ognrfeibnr">
        <div ref={stageRef} data-wavelength-stage data-active-wavelength={selected.id} className="sticky overflow-hidden rounded-xl text-white" style={{
        top: stickyTop,
        height: stageHeight,
        backgroundColor: selected.background
      }} data-id="element-367" data-mp="fn11anh5a26t">
          <div className="grid h-full grid-cols-[1fr_1.1fr] items-stretch" data-id="element-368" data-mp="picfn4on57e1">
            <div className="flex min-h-0 flex-col justify-center px-8 py-9 xl:px-12" data-id="element-369" data-mp="uc6yk5wf9ic2">
              <p className="text-[13px] uppercase tracking-[.12em]" style={{
              color: selected.color
            }} data-id="element-370" data-mp="z9fgc5dnrkcs">0{active + 1} / 04 · {selected.name}</p>
              <p className="mt-5 font-display text-[64px] leading-none xl:text-[80px]" data-id="element-371" data-mp="18andul8coh5t">{selected.wavelength}<span className="ml-2 font-sans text-[18px] text-white/70" data-id="element-372" data-mp="188u2wtqpfas0">nm</span></p>
              <h3 className="mt-6 font-display text-[34px] font-medium leading-tight xl:text-[42px]" data-id="element-373" data-mp="1d7nj4txdlfvv">{selected.title}</h3>
              <p className="mt-4 max-w-md text-[16px] leading-relaxed text-white/90" data-id="element-374" data-mp="3f138zoln2qps">{selected.body}</p>
              <p className="mt-3 max-w-md text-[13px] leading-relaxed text-white/70" data-id="element-375" data-mp="3705enuwl17me">{selected.detail}</p>
              <div className="mt-7 grid grid-cols-4 gap-2" role="group" aria-label="Choose wavelength" data-id="element-376" data-mp="32zbsp72pa2w5">
                {wavelengths.map((item, index) => <button type="button" key={item.id} onClick={() => goToStep(index)} aria-pressed={active === index} className="min-h-[48px] border-b-2 px-1 py-2 text-left text-[13px] font-medium focus-visible:outline-white" style={{
                borderColor: active === index ? item.color : '#FFFFFF40',
                color: active === index ? item.color : '#FFFFFFB3'
              }} data-id="element-377" data-mp="3h0631v9b6dxg" data-mpi={index}>
                  {item.wavelength}<span className="ml-1 text-[10px]" data-id="element-378" data-mp="3j6t1gdhtb5t">nm</span>
                </button>)}
              </div>
            </div>
            <LightVisual item={selected} data-id="element-379" data-mp="3us2q3kw4dxmz" />
          </div>
        </div>
      </div>

      <div data-wavelength-mobile style={{
      display: isPinned ? 'none' : undefined
    }} data-id="element-380" data-mp="di4do87sskjj">
        <div ref={mobileVisual} data-wavelength-mobile-visual data-active-wavelength={selected.id} className="sticky top-[calc(var(--site-header-height)_+_var(--prototype-top-offset,0px)_+_1px)] z-20 text-white md:top-[calc(var(--site-header-height)_+_1px)]" style={{
        backgroundColor: selected.background
      }} data-id="element-381" data-mp="8k2xpw5o51ca">
          <p className="absolute left-5 top-4 z-20 text-[12px] font-semibold uppercase tracking-[.1em]" style={{
          color: selected.color
        }} data-id="element-382" data-mp="i8hwl7vfflpo">{selected.name} · {selected.wavelength} nm</p>
          <LightVisual item={selected} compact data-id="element-383" data-mp="8f14q9euogon" />
        </div>
        <ol className="mx-auto max-w-page px-5 md:px-10" data-id="element-384" data-mp="d7u4eivgoytv">
          {wavelengths.map((item, index) => <li key={item.id} ref={element => {
          mobileSteps.current[index] = element;
        }} data-wavelength-mobile-step={item.id} className="flex min-h-[300px] flex-col justify-center border-b border-line py-8 md:min-h-[250px]" style={{
          scrollMarginTop: 'calc(var(--site-header-height) + var(--prototype-top-offset, 0px) + 240px)'
        }} data-id="element-385" data-mp="3vltodjr8zohw" data-mpi={index}>
            <p className="text-[12px] font-medium uppercase tracking-[.1em] text-bronze-deep" data-id="element-386" data-mp="3qlfia797iomi">0{index + 1} / 04 · {item.name} · {item.wavelength} nm</p>
            <h3 className="mt-3 font-display text-[32px] font-medium leading-tight text-ink" data-id="element-387" data-mp="5wzag87j5zmm">{item.title}</h3>
            <p className="mt-3 max-w-xl text-[16px] leading-relaxed text-ink-muted" data-id="element-388" data-mp="x36ua2qwa7l">{item.body}</p>
            <p className="mt-3 max-w-xl text-[13px] leading-relaxed text-ink-muted" data-id="element-389" data-mp="3s3e19nd92cpy">{item.detail}</p>
          </li>)}
        </ol>
      </div>
      <div className="mx-auto max-w-page px-5 pb-14 pt-7 md:px-10" data-id="element-390" data-mp="3n7gv29vpamqw">
        <p className="max-w-3xl text-[13px] leading-relaxed text-ink-muted" data-id="element-391" data-mp="3iduh8edswcti">Light-therapy research varies by device and treatment. Results vary. Follow the mask’s manual and speak with your dermatologist about suitability.</p>
        <p className="mt-2 text-[13px] text-ink-muted" data-id="element-392" data-mp="39buynhn510og">Learn more: <a href="https://www.aad.org/public/cosmetic/safety/red-light-therapy" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ink" data-id="element-393" data-mp="34f4n97ma2t9d">American Academy of Dermatology: red light</a> · <a href="https://www.aad.org/public/diseases/acne/derm-treat/lasers-lights" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ink" data-id="element-394" data-mp="2pathacwukqlk">Light-based acne care</a></p>
      </div>
    </section>;
}